import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import en from "./en.json";
import fi from "./fi.json";
import sv from "./sv.json";
import { LANGS, LOCALE_MAP, getStoredLang, setStoredLang } from "./locale";

const RESOURCES = { en, fi, sv };

function resolve(obj, key) {
  return key.split(".").reduce((acc, part) => (acc == null ? undefined : acc[part]), obj);
}

function interpolate(str, vars) {
  if (typeof str !== "string" || !vars) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ""));
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(getStoredLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Re-apply language when the cloud sync layer hydrates settings after login.
  useEffect(() => {
    const onSettings = () => setLangState(getStoredLang());
    window.addEventListener("chronicles:settings-changed", onSettings);
    return () => window.removeEventListener("chronicles:settings-changed", onSettings);
  }, []);

  const setLang = useCallback((next) => {
    if (!LANGS.includes(next)) return;
    setStoredLang(next);
    setLangState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      let val = resolve(RESOURCES[lang], key);
      if (val == null) val = resolve(RESOURCES.en, key); // fall back to English
      if (val == null) return key;
      if (typeof val === "string") return interpolate(val, vars);
      return val; // arrays / objects (e.g. weekday lists)
    },
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, locale: LOCALE_MAP[lang] || "en-GB" }),
    [lang, setLang, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useTranslation must be used within an I18nProvider");
  return ctx;
}

export { LANGS, LOCALE_MAP };
